import { useSearch } from './useSearch'
import { Drawer } from '@lounge-fe/ui-kit'
import { Search } from '../Search'
import { ShopifySectionComponent } from '@/types/ShopifySectionComponent'
import { HeaderData } from './types'
import { Basket } from '../Basket'
import { StandardProductCard } from '@/features/product/components/StandardProductCard'
import {
  ClickedProductAfterSearchEvent,
  ConvertedObjectIDsAfterSearchEvent,
  useSearchInsights,
} from '@/hooks/useSearchInsights'
import { useAuth } from '@lounge-fe/auth'
import { Navigation } from '@/features/navigation/components/Navigation'
import { useEffect, useMemo, useState } from 'react'
import { NavigationDrawer } from '@/features/navigation/components/NavigationDrawer'
import { HomeNavigation } from '@/features/navigation/components/NavigationDrawer/HomeNavigation'
import { SubNavigation } from '@/features/navigation/components/NavigationDrawer/SubNavigation'
import { FiltersDrawer } from '@/components/FiltersDrawer/FiltersDrawer'
import {
  useFilterSections,
  useSortOptions,
} from '@/components/FiltersDrawer/hooks'
import type {
  BaseFilterValue,
  FilterForm,
} from '@/components/FiltersDrawer/types'
import { mapValuesToFacets } from '@/components/FiltersDrawer/utils'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useSearchParamsSync } from '@/pages/Collection/useSearchParamsSync'
import { algoliaDefaultValues } from '@/services/algolia'
import { useTranslation } from 'react-i18next'

export const Header: ShopifySectionComponent<HeaderData> = ({ data }) => {
  const { status: authStatus } = useAuth()
  const searchTiles = (data?.searchTiles ?? []).filter(({ image }) =>
    Boolean(image),
  )
  const menuItems = useMemo(() => data?.menuItems ?? [], [data?.menuItems])
  const { defaultValues, updateSearchParams } = useSearchParamsSync<FilterForm>(
    {
      defaultValues: algoliaDefaultValues,
    },
  )

  const [filters, setFilters] = useState(mapValuesToFacets(defaultValues))
  const { getFlag } = useFeatureFlags()
  const isSearchFiltersEnabled = getFlag('enable_search_filters')

  const {
    query,
    isSearchOpen,
    onSearchChange,
    availableFilters,
    hits,
    isLastPage,
    showMore,
    nbHits,
    suggestedSearchTerms,
  } = useSearch({
    isSearchFiltersEnabled,
    filters,
    suggestedSearchTermFallbacks: data?.suggestedSearchTermFallbacks ?? [],
  })

  const { clickedProductAfterSearch, convertedObjectIDsAfterSearch } =
    useSearchInsights()

  const facets: { [key: string]: BaseFilterValue[] } = availableFilters ?? {}
  const { filterSections } = useFilterSections(facets)

  const { t } = useTranslation()

  const handleFiltersChanged = (values: FilterForm) => {
    updateSearchParams(values)
    setFilters(mapValuesToFacets(values))
  }

  return (
    <Navigation
      basketComponent={
        <Basket
          emptyBagAction={data?.emptyBagAction}
          choiceChipQuickAdd={data?.cartChoiceChipQuickAdd}
          cartDiscountProgress={data?.cartDiscountProgress}
        />
      }
      searchComponent={
        <Search
          isFull={query.length > 0}
          isOpen={isSearchOpen}
          className={!isSearchFiltersEnabled ? 'py-2 xl:pt-0' : undefined}
        >
          {query.length > 0 && (
            <>
              {isSearchFiltersEnabled ? (
                <>
                  <div className="text-center w-full py-2">
                    <Search.Stats query={query} count={nbHits} />
                  </div>
                  <div className="md:flex-row justify-between sticky z-[60] bg-white top-16 md:top-20 md:py-4">
                    <FiltersDrawer
                      title={t('action.filter')}
                      drawerTitle={t('action.filter_by')}
                      className="py-4 md:border-t-0 md:py-0"
                      filterSections={filterSections}
                      defaultValues={defaultValues}
                      onSubmit={handleFiltersChanged}
                    />
                  </div>
                </>
              ) : (
                <div className="mb-8">
                  <Search.Stats query={query} count={nbHits} />
                </div>
              )}

              <Search.Grid>
                {hits.map((hit, idx) => (
                  <StandardProductCard
                    product={hit}
                    key={hit.id}
                    useImageCarousel
                    useQuickAdd
                    onClick={() => {
                      if (hit.objectID) {
                        clickedProductAfterSearch({
                          objectID: hit.objectID,
                          eventName: ClickedProductAfterSearchEvent.Search,
                          queryID: hit.queryID,
                          position: idx + 1,
                        })
                      }
                    }}
                    onQuickAdd={() => {
                      if (hit.queryID && hit.objectID) {
                        convertedObjectIDsAfterSearch({
                          objectID: hit.objectID,
                          eventName: ConvertedObjectIDsAfterSearchEvent.Search,
                          queryID: hit.queryID,
                        })
                      }
                    }}
                    showWishlist={authStatus === 'authenticated' ? true : false}
                  />
                ))}
              </Search.Grid>
              {!isLastPage && (
                <Search.LoadMore
                  currentHits={hits.length}
                  totalHits={nbHits}
                  onClick={() => showMore()}
                />
              )}
            </>
          )}
          {query.length === 0 && (
            <Search.Menu
              tiles={searchTiles.map((tile) => ({
                href: tile.link,
                image: tile.image,
                title: tile.title,
              }))}
            >
              <Search.Recommendations
                suggestedSearchTerms={suggestedSearchTerms}
              />
            </Search.Menu>
          )}
        </Search>
      }
      onSearchSubmit={(value) => onSearchChange(value)}
    >
      <NavigationDrawer.Drawer>
        {({ onOpen }) => (
          <>
            <Navigation.Header onMenuClick={onOpen} />
            <Drawer.Pagination>
              <NavigationDrawer.Content>
                <HomeNavigation
                  menuItems={menuItems}
                  drawerFooterMenu={data?.drawerFooterMenu}
                />
                <SubNavigation menuItems={menuItems} tiles={data?.tiles} />
              </NavigationDrawer.Content>
            </Drawer.Pagination>
          </>
        )}
      </NavigationDrawer.Drawer>
    </Navigation>
  )
}
